import "../UserManagement.css";
import { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import {
    UserPlus,
    Search,
    Filter,
    Users,
    Shield
} from "lucide-react";
import CardPagination from "../CardPagination";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import { myLocalStorage } from "../../StorageHelper";
import { getUsers, organizationUnits, registerdUsers, sendInvite, userInvites } from "./Api/activedirectoryapi";

interface ADUser {
    id: string;
    name: string;
    organisationUnit: string;
    distinguishedName: string;
    samAccountName: string;
    userPrincipalName: string;
    emailAddress: string;
    verificationCode: string;
    isRegistered: boolean;
}


const ITEMS_PER_PAGE = 10;

export default function InviteAdUsers() {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const [adUsers, setAdUsers] = useState<ADUser[]>([]);

    // Filter and search logic
    const filteredUsers = useMemo(() => {
        let filtered = adUsers;
        console.log(adUsers)

        // Apply registration filter
        if (filterType === "registered") {
            filtered = filtered.filter(user => user.isRegistered);
        } else if (filterType === "unregistered") {
            filtered = filtered.filter(user => !user.isRegistered);
        }

        // Apply search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(searchLower) ||
                user.samAccountName.toLowerCase().includes(searchLower) ||
                user.emailAddress.toLowerCase().includes(searchLower) ||
                user.userPrincipalName.toLowerCase().includes(searchLower)
            );
        }

        return filtered;
    }, [searchTerm, filterType, adUsers]);


    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleFilterChange = (value: string) => {
        setFilterType(value);
        setCurrentPage(1);
    };

    const handleSelectUser = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        const currentPageUserIds = paginatedUsers.map(user => user.id);
        if (checked) {
            // Add all current page users to selection (preserve other page selections)
            setSelectedUsers(prev => {
                const combined = [...prev, ...currentPageUserIds];
                return Array.from(new Set(combined));
            });
        } else {
            // Remove all current page users from selection (preserve other page selections)
            setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
        }
    };

    const handleInviteUser = async (user: ADUser) => {
        if (user.isRegistered) {
            console.log("User already registered:", user.name);
            return;
        }

        try {
            const fullName = user.name;
            const spaceIndex = fullName.indexOf(" ");
            const firstName = fullName.substring(0, spaceIndex);
            const lastName = fullName.substring(spaceIndex + 1);
            let payload = {
                firstName: firstName,
                lastName: lastName,
                email: user.emailAddress,
                tenantName: tenantName,
            };
            await sendInvite(payload);
        } catch (e) {

        }



        console.log("Inviting user:", user.name);
        // Here you would implement the invite logic
    };

    const handleBulkInvite = () => {
        const usersToInvite = selectedUsers
            .map((userId: string) => adUsers.find((u: any) => u.id === userId))
            .filter(
                (user: any) =>
                    user &&
                    !user.isRegistered &&
                    user.emailAddress
            );

        if (usersToInvite.length === 0) {
            console.log("No eligible users to invite.");
            return;
        }

        const invitePromises = usersToInvite.map((user: any) => {
            const fullName = user.name || "";
            const spaceIndex = fullName.indexOf(" ");
            const firstName = spaceIndex !== -1 ? fullName.substring(0, spaceIndex) : fullName;
            const lastName = spaceIndex !== -1 ? fullName.substring(spaceIndex + 1) : "";

            const payload = {
                firstName,
                lastName,
                email: user.emailAddress,
                tenantName: tenantName,
            };

            return sendInvite(payload);
        });

        console.log("Bulk inviting unregistered users:", usersToInvite);

        Promise.all(invitePromises)
            .then(() => {
                console.log("Bulk invite successful");
            })
            .catch((error) => {
                console.error("Error during bulk invite:", error);
            });
    };


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };



    const combineData = (
        units: any[],
        users: any[],
        registeredUsers: any[],
        invitedUsers: any[],
    ): any[] => {
        return users.map((user: any): any => {
            const matchedUnit = units.find((unit: any) =>
                user.DistinguishedName?.includes(unit.distinguishedName)
            );

            const registeredUser = user.EmailAddress
                ? registeredUsers.find(
                    (reg: any) => reg.email?.toLowerCase() === user.EmailAddress?.toLowerCase()
                )
                : null;

            const invitedUser = user.EmailAddress
                ? invitedUsers.find(
                    (inv: any) => inv.email?.toLowerCase() === user.EmailAddress?.toLowerCase()
                )
                : null;

            return {
                id: user.SamAccountName || user.UserPrincipalName || user.DistinguishedName,
                name: user.Name || "",
                organisationUnit: matchedUnit ? matchedUnit.name : "",
                distinguishedName: user.DistinguishedName || "",
                samAccountName: user.SamAccountName || "",
                userPrincipalName: user.UserPrincipalName || "",
                emailAddress: user.EmailAddress || "",
                verificationCode: invitedUser?.verificationCode || "",
                isRegistered: !!registeredUser,
            };
        });
    };



    const fetchDetails = async () => {
        const [units, users, registeredUsers, invitedUsers] = await Promise.all(
            [
                organizationUnits(tenantName),
                getUsers(tenantName),
                registerdUsers(tenantName),
                userInvites(tenantName),
            ],
        );
        const combinedData = combineData(
            units,
            users,
            registeredUsers,
            invitedUsers,
        );
        setAdUsers(combinedData);
    }

    useEffect(() => {
        fetchDetails();
    }, [tenantName])



    const invitableSelectedCount = selectedUsers.filter(userId => {
        const user = adUsers.find(u => u.id === userId);
        return user && !user.isRegistered;
    }).length;

    return (
        <div className="um-page um-directory-page">
            <div className="um-card um-toolbar um-directory-toolbar">
                <div className="um-title">
                    <span className="um-title-icon" aria-hidden="true">
                        <Shield />
                    </span>
                    <h1 className="um-title-text" data-testid="text-ad-users-title">
                        Active Directory Users
                        <span className="um-title-count">{paginatedUsers.length}</span>
                    </h1>
                </div>
                <div className="um-toolbar-actions">
                    <Select value={filterType} onValueChange={handleFilterChange}>
                        <SelectTrigger className="um-select" aria-label="Filter users" data-testid="select-filter">
                            <Filter />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Show All</SelectItem>
                            <SelectItem value="registered">Registered Users</SelectItem>
                            <SelectItem value="unregistered">Unregistered Users</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="um-search">
                        <Search aria-hidden="true" />
                        <Input
                            placeholder="Search..."
                            aria-label="Search Active Directory users"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            data-testid="input-search-ad-users"
                        />
                    </div>
                    <EndpointActionButton
                        onClick={handleBulkInvite}
                        disabled={invitableSelectedCount === 0}
                        data-testid="button-invite-selected"
                    >
                        <UserPlus className="w-4 h-4" />
                        Invite Now ({invitableSelectedCount})
                    </EndpointActionButton>
                </div>
            </div>

            <div className="um-card um-table-card">
                <div className="um-table-scroll">
                    <Table className="um-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.includes(user.id))}
                                        onCheckedChange={handleSelectAll}
                                        aria-label="Select all users on this page"
                                        data-testid="checkbox-select-all"
                                    />
                                </TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>User AD Details</TableHead>
                                <TableHead>Email Address</TableHead>
                                <TableHead className="text-center">Verification Code</TableHead>
                                <TableHead className="text-center">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.length > 0 ? paginatedUsers.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selectedUsers.includes(user.id)}
                                            onCheckedChange={() => handleSelectUser(user.id)}
                                            aria-label={`Select ${user.name}`}
                                            data-testid={`checkbox-user-${user.id}`}
                                        />
                                    </TableCell>
                                    <TableCell data-testid={`name-${user.id}`}>
                                        <div className="um-identity">
                                            <span className="um-avatar" aria-hidden="true">
                                                {user.name?.[0] ?? ""}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="um-name">{user.name}</div>
                                                {user.isRegistered && (
                                                    <span className="um-chip mt-1" data-tone="success">Registered</span>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell data-testid={`ad-details-${user.id}`} className="max-w-md">
                                        <div className="um-ad-details">
                                            <div className="um-ad-meta">
                                                <span className="um-chip"><em>OU</em>{user.organisationUnit || "—"}</span>
                                                <span className="um-chip" data-mono="true"><em>SAM</em>{user.samAccountName || "—"}</span>
                                                <span className="um-chip"><em>UPN</em>{user.userPrincipalName || "—"}</span>
                                            </div>
                                            <span className="um-ad-dn" title={user.distinguishedName}>
                                                {user.distinguishedName}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell data-testid={`email-${user.id}`}>
                                        {user.emailAddress ? (
                                            <span className="um-email">{user.emailAddress}</span>
                                        ) : (
                                            <span className="um-dash">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center" data-testid={`verification-${user.id}`}>
                                        <div className="um-code justify-center">
                                            <Shield aria-hidden="true" />
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleInviteUser(user)}
                                            disabled={user.isRegistered}
                                            className="um-row-action no-default-hover-elevate"
                                            data-testid={`button-invite-${user.id}`}
                                        >
                                            <UserPlus />
                                            {user.isRegistered ? 'Invited' : 'Invite'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow className="um-state-row">
                                    <TableCell colSpan={6}>
                                        <div className="um-state">
                                            <span className="um-state-icon" aria-hidden="true">
                                                <Users />
                                            </span>
                                            <p className="um-state-title">
                                                {searchTerm ? 'No matching users found' : 'No AD users available'}
                                            </p>
                                            <p className="um-state-text">
                                                {searchTerm
                                                    ? 'Try adjusting your search criteria or filter settings'
                                                    : 'Connect to Active Directory to sync user data'}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {filteredUsers.length > 0 && (
                    <div className="um-pagination">
                        <span className="um-pagination-summary">
                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                        </span>
                        <CardPagination
                            label="Active Directory user pages"
                            currentPage={currentPage}
                            totalPages={Math.max(1, totalPages)}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
